// @flow

import React from "react";
import className from "classnames";
import Form, { type FormProps } from "react-jsonschema-form";
import SchemaField from "react-jsonschema-form/lib/components/fields/SchemaField";

import {
  CheckboxWidget,
  CheckboxesWidget,
  RadioWidget,
  SelectWidget,
  ToggleWidget,
  RadioHierarchyTreeWidget,
  RadioHierarchyTreeModalWidget,
  SecondsAsMinutesInput,
  SecondsAsHoursInput,
  SecondsAsDaysInput,
  SecondsAsWeeksInput,
  DatePickerWidget,
  MultipleTypeaheadSelect,
  NumberInputWidget,
  TextareaWidget,
  TextWidget,
  CheckboxHierarchyTreeWidget,
  FileInputWidget,
  ImageLibraryPickerWidget,
} from "./widgets";
import {
  ErrorTemplate,
  FieldTemplate,
  ObjectTemplate,
  ArrayTemplate,
  ObjectFieldTemplate,
} from "./templates";
import { MultiSelectField } from "./fields";
import { transformInput, transformOutput } from "./utils";
import "./form.style.scss";

const widgets = {
  checkbox: CheckboxWidget,
  checkboxes: CheckboxesWidget,
  radio: RadioWidget,
  select: SelectWidget,
  toggle: ToggleWidget,
  "date-picker": DatePickerWidget,
  "radio-hierarchy-tree": RadioHierarchyTreeWidget,
  "radio-hierarchy-tree-modal": RadioHierarchyTreeModalWidget,
  "seconds-as-minutes-input": SecondsAsMinutesInput,
  "seconds-as-hours-input": SecondsAsHoursInput,
  "seconds-as-days-input": SecondsAsDaysInput,
  "seconds-as-weeks-input": SecondsAsWeeksInput,
  "multiple-typeahead-select": MultipleTypeaheadSelect,
  "number-input": NumberInputWidget,
  textarea: TextareaWidget,
  text: TextWidget,
  "checkbox-hierarchy-tree": CheckboxHierarchyTreeWidget,
  file: FileInputWidget,
  "image-library-picker": ImageLibraryPickerWidget,
};

// Fyi: https://github.com/rjsf-team/react-jsonschema-form/issues/573#issuecomment-299934732
// Include 'ui:field' in ui schemas to use some particular field listed here
const fields = {
  SchemaField,
  "multi-select": MultiSelectField,
};

type Props = $Exact<{
  ...$Exact<FormProps>, // https://stackoverflow.com/a/44771268/220839
  wrapperClassName?: string,
  externalSubmit: boolean,
  twoColumns?: boolean,
  threeColumns?: boolean,
}>;

class SchemaForm extends React.Component<Props> {
  submitButton: *;

  handleSubmit = (e: Object) => {
    if (this.props.onSubmit) {
      this.props.onSubmit(transformOutput(e)); // WARNING !!!
    }
  };

  handleChange = (e: Object) => {
    if (this.props.onChange) {
      this.props.onChange(transformOutput(e)); // WARNING !!!
    }
  };

  get formData() {
    return this.props.dontTransformTheData === true
      ? this.props.formData
      : transformInput(this.props.formData || {}, this.props.schema);
  }

  get errorsMap() {
    return {
      pattern: "INCORRECT_PATTERN",
      required: "REQUIRED_FIELD",
    };
  }

  transformErrors = (errors: Object[]): Object[] => {
    return errors.map((err: Object) => {
      if (this.errorsMap[err.name]) {
        return {
          ...err,
          message: this.errorsMap[err.name],
        };
      }
      if (err.name === "minimum") {
        return {
          ...err,
          message: `Must be greater than or equal to ${err.params.limit}`,
        };
      }
      return err;
    });
  };

  render() {
    return (
      <div className={`schema-form-wrapper w-100 m-0 ${this.props.wrapperClassName || ""}`}>
        <Form
          {...Object.assign({}, this.props, {
            className: className("fc-schema-form", this.props.className, {
              "two-columns": this.props.twoColumns,
              "three-columns d-flex": this.props.threeColumns,
            }),
            widgets,
            fields,
            ObjectFieldTemplate: this.props.threeColumns ? ObjectFieldTemplate : null,
            ObjectTemplate,
            ErrorList: ErrorTemplate,
            FieldTemplate,
            ArrayFieldTemplate: ArrayTemplate,
            transformErrors: this.transformErrors,
            showErrorList: false,
            noHtml5Validate: true,
            liveValidate: false,
            formData: this.formData,
            onSubmit: this.handleSubmit,
            onChange: this.handleChange,
          })}
        >
          {this.props.children && <div className="bottom-section w-100">{this.props.children}</div>}
          {this.props.externalSubmit ? (
            <button
              className="schema-form-submit-button"
              ref={btn => {
                if (btn) {
                  this.submitButton = btn;
                }
              }}
            />
          ) : null}
        </Form>
      </div>
    );
  }
}

export default SchemaForm;
